import PropTypes from 'prop-types';
import { createContext, useEffect, useMemo, useReducer } from 'react';
import { authApi, twoFactorChallenge, twoFactorRecoveryCodes } from '@services/auth-api';

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  checkTwoFactor: false,
  user: null,
  permissions: null,
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user, permissions } = action.payload;
    return { ...state, isAuthenticated, isInitialized: true, user, permissions };
  },
  CHECK_TWO_FACTOR: (state, action) => {
    const { user, permissions } = action.payload;
    return { ...state, isAuthenticated: true, checkTwoFactor: false, user, permissions };
  },
  LOGIN: (state, action) => {
    const { user, permissions } = action.payload;
    return { ...state, isAuthenticated: true, passwordExpired: false, user, permissions };
  },
  REGISTER: (state, action) => {
    const { user, permissions } = action.payload;
    return { ...state, isAuthenticated: true, passwordExpired: false, user, permissions };
  },
  TWO_FACTOR: (state) => ({ ...state, isAuthenticated: true, checkTwoFactor: true }),
  LOGOUT: (state) => ({ ...state, isAuthenticated: false, user: null, permissions: null }),
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

export const AuthContext = createContext({
  ...initialState,
  platform: 'SANCTUM',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  checkToFactorLogin: () => Promise.resolve(),
  checkRecoveryCodes: () => Promise.resolve(),
});

export const AuthProvider = function AuthProvider(props) {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');
        const user = await authApi.me();
        if (accessToken || user != null) {
          const permissions = await authApi.permissions();
          dispatch({ type: 'INITIALIZE', payload: { isAuthenticated: true, user, permissions } });
        } else {
          dispatch({ type: 'INITIALIZE', payload: { isAuthenticated: false, user: null, permissions: null } });
        }
      } catch (err) {
        dispatch({ type: 'INITIALIZE', payload: { isAuthenticated: false, user: null, permissions: null } });
      }
    };

    initialize();
  }, []);

  const login = async (email, password) => {
    const response = await authApi.login({ email, password });
    if (response.two_factor === true) {
      dispatch({ type: 'TWO_FACTOR' });
    } else {
      const user = await authApi.me();
      const permissions = await authApi.permissions();

      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('permissions', JSON.stringify(permissions));

      dispatch({ type: 'LOGIN', payload: { user, permissions } });
    }
  };

  const checkToFactorLogin = async (code) => {
    const response = await twoFactorChallenge(code);
    if (response.status === 404) return response.data.message;
    if (response.status === 204) {
      const user = await authApi.me();
      const permissions = await authApi.permissions();

      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('permissions', JSON.stringify(permissions));

      dispatch({ type: 'CHECK_TWO_FACTOR', payload: { user, permissions } });
    }

    return null;
  };

  const checkRecoveryCodes = async (recoveryCode) => {
    const response = await twoFactorRecoveryCodes(recoveryCode);
    if (response.status === 404) return response.data.message;
    if (response.status === 204) {
      const user = await authApi.me();
      const permissions = await authApi.permissions();

      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('permissions', JSON.stringify(permissions));

      dispatch({ type: 'CHECK_TWO_FACTOR', payload: { user, permissions } });
    }

    return null;
  };

  const logout = async () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    await authApi.logout();
    dispatch({ type: 'LOGOUT' });
  };

  const register = async (email, name, password) => {
    const accessToken = await authApi.register({ email, name, password });
    const user = await authApi.me(accessToken);
    localStorage.setItem('accessToken', accessToken);

    dispatch({ type: 'REGISTER', payload: { user } });
  };

  const authContext = useMemo(
    () => ({
      ...state,
      platform: 'SANCTUM',
      login,
      logout,
      register,
      checkToFactorLogin,
      checkRecoveryCodes
    }),
    [state]
  );

  return <AuthContext.Provider value={authContext}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const AuthConsumer = AuthContext.Consumer;
